/* eslint-disable id-blacklist */
/* eslint-disable @typescript-eslint/naming-convention */
import { ID } from '@datorama/akita';
import { Point, Polygon } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import Map from 'ol/Map';
import { Vector as VectorLayer } from 'ol/layer';
import ImageLayer from 'ol/layer/Image';
import { IAddressExtension, IFeature } from '../models';
import { ViewMode } from './helpers';

export interface AuthPrivilege {
  /**
   * This says the action to trigger: get, allow, post, put
   */
  action: PageAction;

  /**
   * This will get the page where the action want to be triggered
   */
  function: string;

  /**
   * This will always be d2c-central for our purposes
   */
  service: string;

  condition: null;
  fields: null;
  fieldsCondition: null;

  id: string;
}

export enum PageAction {
  allow = 'allow',
  get = 'get',
  post = 'post',
  put = 'put',
  delete = 'delete',
  deactivate = 'deactivate',
}

export interface IMetaData {
  createdAt?: Date;
  modifiedAt?: Date;
  createdBy?: string;
  modifiedBy?: string;
  href?: string;
}

export interface IAssignVendorSalesRep {
  mode: ViewMode;
  label: string;
  value: string | string[];
  editType: string;
  propertyName: string;
  group: string;
  groupId: string;
}

export interface IModel {
  id: ID;
}

export interface IPrivilege extends IModel {
  createdAt: Date;
  modifiedAt: Date;
  createdBy: string;
  modifiedBy: string;
  href: string;
  action: string;
  functionName: string;
  service: string;
  condition: string;
  fieldAccess: IFieldAccess;
  active: boolean;
}

export interface IFieldAccess extends IModel, IMetaData {
  fields: string;
  condition: string;
}

export interface IEntity extends IMetaData {
  id: ID;
}

export interface ISpecialEvent extends IEntity {
  eventName: string;
  salesActivity: string;
  active: boolean;
  startDate: Date;
  endDate: Date;
}

export interface IVendorGroupMember {
  Id: string;
  Federation_ID__c: string;
  IsDeleted: boolean;
  Legal_Name__c: string;
  Name: string;
  Profile__c: string;
  Role__c: string;
  Status__c: string;
  Territory__c: string;
  Vendor_Group__c: string;
  CPMS_ID__c: string;
}

export interface IAddress extends IModel {
  number: string;
  unit: string;
  streetAddress: string;
  city: string;
  clli: string;
  internet: boolean;
  tv: boolean;
  date: Date;
}

export interface IAdress extends IModel {
  name: string;
  author: string;
  date: Date;
}

export interface IConfigSetting extends IModel {
  name: string;
  value: string;
  type: string;
  createdBy: string;
  createdAt: Date;
  modifiedBy: string;
  modifiedAt: Date;
}

export interface Geometry {
  type: string;
  coordinates: any[];
}

export interface IAddressWalklist extends IModel {
  serviceAddress: string;
  fsa: string;
  postalCode: string;
  unitNumber: string;
  streetNumber: string;
  streetName?: string;
  city?: string;
  province?: string;
  streetTypeSuffix?: string;
  suffix: string;
  dirPrefix: string;
  streetNameType: string;
  dirSuffix: string;
  premiseType: string;
  sayb: string;
  permissionStatus: string;
  walklistAssigned: boolean;
  checked: boolean;
  searchStreet: string;
  walklistAssignedTo: string;
  walklistId: number;
  walklistCreationAt: Date;
  walklistModifiedAt: Date;
  walklistCreatedBy: string;
  walklistModifiedBy: string;
  walklistActive: boolean;
  derivedDpStatus: string;
  doNotKnockInd: boolean;
  totalFeatures: number;
}

export interface IBulkAction<T> {
  op: string;
  path: string;
  value: T;
}

export interface IActivityAddress extends IModel {
  addressId: string;
  name: string;
  doNotKnock: boolean;
  language: string;
  salesPremiseType: string;
  dpWalklistId?: number;
  salesWalklistId?: number;
  address: string;
  derivedDpStatus: string;
  createdBy?: string;
}

export interface IGeoServer {
  type: string;
  id: string;
  geometry_name: string;
  geometry: Geometry;
  properties: {
    coid: string;
    province: string;
    polygon_type: string; // FDSA,
    stype: string; // FM_BOUNDARY,
    jms_status: number; // 0,
    published: string; // Y,
    job_id: string; // TELUSBND,
    journal: number; // 0,
    date_published: Date; // 2018-12-13Z,
    owning_drawingc: string;
    owning_drawing: string;
    attr2: string; // LPDS ID
    attr5: Date; // 31 Aug 2017,
    attr6: string; // VANC,
    attr7: string; // VANCOUVER,
    attr8: string; // HMLK,
    attr9: string; // HEMLOCK,
    attr10: string; // FDSA,
    attr11: string; // 1021A,
    attr14: string; // HMLK,
    attr_15: string;
    attr15: string; // HMLKBC,
    attr16: string; // HMLKBC,
    attr21: Date; // 2018-07-01Z,
    attr24: string; // 1021A,
    attr29: string; // KML,
    attr30: string;
    polygon_name: string; // 1021A,
    polygon_contact: string; // PLANNING,
    polygon_city: string;
    polygon_county: string;
    polygon_state: string; // BC,
    alt_name: string;
    myw_geometry_world_name: string; // geo
    premise_sfu_cnt: number;
    premise_mxu_cnt: number;
    premise_sxu_cnt: number;
    premise_mdu_cnt: number;
    premise_other_cnt: number;
    id: string;
    zip: string;
    unit: string;
    street_number: string;
    suffix: string;
    dirprefix: string;
    street_name: string;
    street_type: string;
    dirsuffix: string;
    building_type_bn: string; // premise type
    search_str: string;
    cfsauid: string;
  };
}

export interface IWalklistLDPS extends IModel {
  serviceAddress: string;
  lpds: string;
  premiseType: string;
  fsa: string;
}

export interface IGeoServerCity extends IModel {
  type: string;
  totalFeatures: number;
  features: IGeoServerCityFeatures[];
}

export interface IGeoServerCityFeatures extends IModel {
  type: string;
  id: string;
  geometry: string;
  properties: IGeoServerCityFeaturesProperties;
}

export interface IGeoServerCityFeaturesProperties extends IModel {
  prname: string;
  city_name: string;
}

export interface ITableFilter extends IModel {
  propertyName: string;
  value: any;
}

export enum TERRITORY_DETAILS_UPDATE {
  VENDOR_SALES_REP = 'vendorSalesRep',
}

export enum WALKLIST_DETAILS_UPDATE {
  VENDOR_SALES_REP_NAME = 'vendorSalesRepName',
  VENDOR_SALES_REP = 'vendorSalesRep',
  VENDOR_SALES_REP_NAME2 = 'vendorSalesRepName2',
  VENDOR_SALES_REP2 = 'vendorSalesRep2',
  DESCRIPTION = 'walklistDescription',
}

export interface IWalkListDetailsPageConfig {
  map?: Map;
  isMapView?: boolean;
  zoomLevel?: number;
  totalHouseholds?: number;
  optimizationId?: string;
  shownSegment?: string;
  territoryId?: string;
  walklistId?: string;
  // @ts-ignore
  vectorLayer?: VectorLayer<any>;
  // @ts-ignore
  featureLayer?: ImageLayer<any>;
  selectedWalklist?: any[];
  walklistFeatures?: any[];
  addresses?: IAddressExtension[];
  selectedWalklistId?: string;
  currentWalklist?: any;
  pickList?: any[];
  detailSections?: any[];
  insightsFirst?: any[];
  insightsSecond?: any[];
  insightsThird?: any[];
  actions?: string[];
  actionsWalkList?: string[];
  showGenerateWalklists?: boolean;
  isLoadingWalklists?: boolean;
  alertInfo?: any;
}

export enum MODAL_CSS_CLASSES {
  TERRITORY_DETAILS_EDIT = 'telus-territory-details-edit-modal',
}

export enum LOCAL_STORAGE_KEYS {
  vendorGroupInfo = 'vendorGroupInfo',
}
